<template>
  <div ref="viewer" class="viewer" :style="viewerBox" :class="viewerclass">
    <div class="viewer_wrapper" :class="themeClass">
      <div v-if="alertMessage" class="alert-message">
        <i class="material-icons">warning</i>
        <span>{{ alertMessage }}</span>
      </div>
      <div class="cling-error" v-if="error">{{error}}</div>
      <video
        ref="video"
        v-if="showVideo"
        style="width:100%"
        class="viewer-video"
        @durationchange="onDurationChange"
        :id="randomVideoId"
        playsinline
      >
        <track
          v-for="subtitle in state.subtitles"
          v-bind:key="subtitle.lang"
          :srclang="subtitle.lang"
          :src="subtitleProxy + subtitle.file"
          :default="showSubtitles && subtitle.lang === selectedLanguage"
        />
      </video>
      <overlay
        ref="overlay"
        :style="overlayStyle"
        :poster="posterUrl"
        :originalVideoDimension="originalVideoDimension"
        :bb="boundingBox"
        :video-id="getVideoId"
        :tags="tags"
        :on-activate="onActivate"
        v-on:open="onOpen"
        v-on:action="onAction"
      ></overlay>
      <div v-if="!showSplash">
        <controls
          class="viewer-controls"
          :showScrubbar="showScrubbar"
          :hasSubtitles="hasSubtitles"
          :showSubtitles="showSubtitles"
          :isPlaying="isVideoPlaying"
          v-on:play="play"
          v-on:pause="pause"
          v-on:overlay="overlay"
          v-on:mute="onMute"
          :tags="tags"
          v-on:fullscreen="fullscreen"
          :video-id="getVideoId"
          :videoDuration="videoDuration"
        ></controls>
      </div>
      <div v-if="showSplash" class="play-splash">
        <!--
        <h1 class="splash-title">
          {{ state.meta.title }}
        </h1>
        -->
        <i class="play-icon material-icons" @click="onStart">play_arrow</i>
        <div style="display:none" class="time">
          <span>02:24</span>
        </div>
      </div>
      <div v-if="showOutro" class="splash">
        <h1 class="splash-title">next video</h1>
        <i class="play-icon material-icons" @click="onPlayNext">play_circle_filled</i>
      </div>
      <div v-if="iframeUrl" class="embedIFrame">
        <iframe :src="iframeUrl" width="100%" height="100%" frameborder="0" style="border:0"></iframe>
        <div class="close iframe" @click="onCloseIframe">
          <i class="material-icons">close</i>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import overlay from "./overlay";
import controls from "./controls";
import uuid from "../../utils/guid";
import screenfull from "screenfull";
import {
  isInScormLMS,
  initScormSession,
  finishScormSession,
  saveScormData,
  getScormData,
  setScore
} from "./scormintegration";
import { apiBaseUrl } from "../../config";
import { base } from "./mixins/multilang";
import GA from "./analytics/ga/";
import { analytics } from "./mixins/analytics/";
import Hls from "hls.js";

// global hls instance when using adaptive playback
var hlsInstance;

export default {
  name: "viewer",
  mixins: [base, analytics],
  props: {
    videoId: {
      type: String,
      default: function() {
        return null;
      }
    },
    videoSource: String,
    state: {
      type: Object,
      default: function() {
        return window.cling;
      }
    },
    environment: {
      type: String,
      default: "editor"
    },
    /* onActivate is used to set the video-behaviour while action. following interpretations are implemented
     * |  '0.5'  | any parseable float will be set as videoPlaybackRate
     * | 'pause' | pauses the video till activeTag gets deactivated
     * |         | any other value don't has implications
     */
    onActivate: {
      type: String,
      default: function() {
        return "play";
      }
    }
  },
  data() {
    return {
      visible: true,
      randomVideoId: uuid(),
      isFullscreen: false,
      isVideoPlaying: false,
      showSubtitles: false,
      error: false,
      videoDuration: 0,
      // depending on the action we do want to supress or allow the play splash
      // i.e. on hover pause on an element should not show the splash, bu open new frame in black window should
      skipSplash: true,
      videoEnded: false,
      iframeUrl: null,
      alertMessage: null,
      boundingBox: null,
      selectedLanguage: null,
      hasSubtitles: false,
      inLMS: false,
      subtitleProxy: `${apiBaseUrl}/api/subtitle?src=`,
      posterUrl: ""
    };
  },
  beforeCreate(data) {
    console.log("beforeCreate", data);
    try {
      console.log("viewer", this.$root, this.$root.use);
      let state = this.$options.propsData.state || {};
      // needs to be here, as we do have a different state system, editor vs player
      // installs GA on root component
      if (
        state.analytics &&
        state.analytics.type === "ga" &&
        state.analytics.code
      ) {
        GA.install(this.$root, {
          assetId: state.assetId,
          assetTitle: state.assetTitle,
          trackingId: state.analytics.code
        });
      }
    } catch (error) {
      console.log("error", error);
    }
  },
  created() {
    // this.sessionStartTime = (new Date()).getTime()
    try {
      if (screenfull) {
        screenfull.onchange(() => {
          this.isFullscreen = screenfull.isFullscreen;
          this.$nextTick(() => {
            this.$refs.overlay.resize("fs");
          });
        });
      }

      this.posterUrl = this.state.meta.poster;
      this.inLMS = isInScormLMS();
      console.log("is in LMS", this.inLMS);
      if (this.inLMS) {
        initScormSession();
        let scormState = getScormData();
        if (scormState !== "") {
          this.state = JSON.parse(scormState);
        }
      }

      // see multilang mixin that handles text changes
      this.$root.$_languages = this.state.meta.languages;
      this.$root.$_selectedLanguage =
        this.state.meta.defaultLanguage || this.state.meta.languages[0] || "de";
      this.selectedLanguage = this.$root.$_selectedLanguage;
      this.showSubtitles = this.state.meta.showSubtitles || false;
      if (this.state.subtitles) {
        this.state.subtitles.map(subtitle => {
          if (subtitle.file !== "") {
            this.hasSubtitles = true;
          }
        });
      }

      this.tags.map(tag => {
        tag.meta.showTag =
          tag.meta.initalShow !== undefined ? tag.meta.initalShow : true;
      });

      this.trackVideo("load", 0);
      console.log("viewer created");
    } catch (error) {
      console.log(error);
    }
  },
  mounted() {
    console.log("viewer mounted");
    this.applyVideoSource(this.src);

    this.setCrossOrigin();

    this.playAt = this.getQueryVariable("playAt");
    this.skipSplash =
      (!this.state.meta.title && !this.state.meta.poster) || this.playAt > 0;
    if (!this.skipSplash) {
      this.$refs.video.autoplay = false;
    }
    this.$refs.video.addEventListener("ended", this.onVideoEnded);

    if (this.state.currentTime) {
      this.$refs.video.currentTime = this.state.currentTime;
    } else if (this.playAt > 0) {
      this.$refs.video.currentTime = this.playAt;
    }
    // listen to child messages
    window.addEventListener("message", this.handleMessage, false);
    this.$refs.video.addEventListener("play", this.onVideoPlay);
    this.$refs.video.addEventListener("pause", this.onVideoPause);
    if (this.state.meta.isMuted) {
      this.$refs.video.setAttribute("muted", "muted");
    }
    if (this.state.meta.isAutoplay) {
      this.$refs.video.setAttribute("autoplay", "autoplay");
    }
  },
  beforeDestroy() {
    this.$refs.video.removeEventListener("ended", this.onVideoEnded);
    window.removeEventListener("message", this.handleMessage, false);
    this.$refs.video.removeEventListener("play", this.onVideoPlay);
    this.$refs.video.removeEventListener("pause", this.onVideoPause);

    this.onSaveScormData(this.$refs.video.currentTime);
    // reset language
    this.$root.$_selectedLanguage = undefined;
  },
  computed: {
    src: function() {
      return this.videoSource
        ? this.videoSource
        : this.state && this.state.src
        ? this.state.src
        : "";
    },
    tags: function() {
      return this.state && this.state.tags;
    },
    getVideoId: function() {
      return this.videoId ? this.videoId : this.randomVideoId;
    },
    showVideo: function() {
      return (
        this.getVideoId === this.randomVideoId &&
        this.src &&
        this.src.length > 0
      );
    },
    overlayStyle() {
      return this.visible ? { display: "flex" } : { display: "none" };
    },
    viewerclass() {
      return this.isFullscreen ? "fullscreen" : "";
    },
    themeClass() {
      return this.state.meta.theme ? this.state.meta.theme + "-theme" : "";
    },
    showSplash() {
      return !this.isVideoPlaying && !this.skipSplash; // this.state.meta.title && this.state.meta.title.length && !this.skipSplash
    },
    showScrubbar() {
      return this.state.meta.showScrubbar;
    },
    showOutro() {
      return this.state.meta.linkNext && this.videoEnded;
    },
    originalVideoDimension() {
      return this.state.dimensions;
    },
    viewerBox() {
      // outside editor, abort
      if (this.environment !== "editor") {
        return null;
      }
      let vw = this.state.meta.width;
      let vh = this.state.meta.height;
      // portrait in editor
      if (vw < vh) {
        let mh = Math.min(window.innerHeight, vh);
        let fh = mh / vh;
        vh = fh * vh;
        vw = fh * vw;
        return {
          width: `${vw}px`
        };
      } else {
        return {
          // width: `${vw}px`,
          "max-width": "100%"
        };
      }
    }
  },
  watch: {
    src: "applyVideoSource"
  },
  components: { overlay, controls },
  methods: {
    applyVideoSource(vidSrc) {
      console.log("applyVideoSource", vidSrc, this.environment);
      // vidSrc = 'https://api.video.swisstxt.ch/p/161/sp/16100/playManifest/entryId/0_uu1a1bnv/format/applehttp/protocol/https/a.m3u8'
      // based on the available video sources use adaptive mode if supported
      let hlsSrc = this.getAdaptiveSource();
      if (hlsSrc && Hls.isSupported()) {
        // if (this.environment === 'turned off for now' && hlsSrc && Hls.isSupported()) {
        // assure singleton
        if (!hlsInstance) {
          hlsInstance = new Hls({
            enableWebVTT: this.hasSubtitles ? false : true,
            enableCEA708Captions: this.hasSubtitles ? false : true,
            startLevel: 3
          });
          hlsInstance.startLevel = 3;
        } else {
          hlsInstance.destroy();
          hlsInstance = new Hls();
          hlsInstance.startLevel = 3;
        }
        if (hlsInstance.subtitleTrackController) {
          let stc = hlsInstance.subtitleTrackController;
          // console.log("Got subttitle tracks", hlsInstance.subtitleTrack, hlsInstance.subtitleTracks)
          stc.onMediaAttached(function(ev) {
            // console.log("subtitle track loaded", ev)
            // console.log("Got subttitle tracks", hlsInstance.subtitleTrack, hlsInstance.subtitleTracks)
            stc.subtitleDisplay = true;
          });
        }
        hlsInstance.loadSource(hlsSrc);
        hlsInstance.attachMedia(this.$refs.video);
      } else {
        this.$refs.video.src = vidSrc;
      }
    },
    getAdaptiveSource() {
      if (this.state && this.state.adaptive && this.state.adaptive.length > 0) {
        return this.state.adaptive[0].url;
      }
      return null;
    },
    play(time) {
      if (time > 0) {
        this.$refs.video.currentTime = time;
        this.onSaveScormData(time);
      }
      this.$refs.video.play();
      this.videoEnded = false;

      this.tags.map(tag => {
        tag.meta.showTag =
          tag.meta.initalShow !== undefined ? tag.meta.initalShow : true;
      });
    },
    setCrossOrigin() {
      //Check if subtitle file is defined, set crossOrigin anonymous
      if (this.state.subtitles) {
        this.$refs.video.crossOrigin = null;
        this.state.subtitles.map(subtitle => {
          if (subtitle && subtitle.file !== "") {
            this.$refs.video.crossOrigin = "anonymous";
          }
        });
      }
    },
    // observe video play
    onVideoPlay() {
      this.isVideoPlaying = true;
      this.skipSplash = true;
      this.posterUrl = "";
      this.trackVideo("play", this.$refs.video.currentTime);
    },
    onSaveScormData(time) {
      if (this.inLMS) {
        this.state.currentTime = time;
        saveScormData(JSON.stringify(this.state));
      }
    },
    getQuizScore() {
      let totalQuiz = 0;
      let correctAnswered = 0;
      if (this.state.tags) {
        this.state.tags.map(tag => {
          if (tag.meta) {
            if (tag.meta.quiz) {
              if (tag.meta.quiz.isCorrectAnswered) {
                correctAnswered++;
              }
              totalQuiz++;
            }
            if (tag.meta.quizzes) {
              tag.meta.quizzes.map(quiz => {
                if (quiz.isCorrectAnswered) {
                  correctAnswered++;
                }
                totalQuiz++;
              });
            }
          }
        });
      }

      return (totalQuiz !== 0
        ? (1 / totalQuiz) * correctAnswered * 100
        : 100
      ).toString();
    },
    onVideoPause() {
      this.isVideoPlaying = false;
      this.trackVideo("pause", this.$refs.video.currentTime);
    },
    pause() {
      this.$refs.video.pause();
    },
    overlay(show) {
      this.visible = show;
    },
    onMute(mute) {
      console.log("muted", mute);
      this.$refs.video.muted = mute;
    },
    fullscreen() {
      screenfull.toggle(this.$refs.viewer);
    },
    onDurationChange(ev) {
      console.log("onDurationChange", ev.target.duration);
      this.videoDuration = ev.target.duration;
    },
    onStart() {
      this.$refs.video.play();
      this.skipSplash = true;
    },
    onPlayNext() {
      console.log("onPlayNext");
      window.location.href = this.state.meta.linkNext;
    },
    onOpen(value) {
      console.log("viewer on open url", value);
      if (isNaN(value.value)) {
        let url = value.value[this.selectedLanguage];
        if (value.target === "iframe") {
          this.iframeUrl = url;
        } else if (value.target === "_self") {
          window.location.href = url;
        } else if (value.target === "_blank" && value.value) {
          // force the splash on video pause event
          this.skipSplash = false;
          this.$refs.video.pause();
          window.open(url, value.target);
        }
      } else {
        this.$refs.video.play();
        this.$refs.video.currentTime = parseFloat(value.value);
      }
    },
    onAction(value) {
      console.log("viewer on action", value);
      if (value.action === "open") {
        this.onOpen(value);
        this.handleModifier(value);
      } else if (value.action === "open-asset") {
        this.handleAssetLoad(value);
      } else if (value.action === "jumpTo") {
        this.$refs.video.currentTime = parseFloat(value.value);
        this.handleModifier(value);
      } else if (value.action === "sleep") {
        const ms = parseInt(value.value);
        if (typeof ms === "number") {
          this.$refs.video.pause();
          setTimeout(() => {
            this.$refs.video.play();
          }, ms);
        }
      } else if (value.action === "play") {
        this.$refs.video.play();
      } else if (value.action === "pause") {
        this.$refs.video.pause();
      } else if (value.action === "toggle") {
        if (this.$refs.video.paused) {
          this.$refs.video.play();
        } else {
          this.$refs.video.pause();
        }
      } else if (value.action === "post-message") {
        this.handlePostMessage(value);
      } else if (value.action === "toggle-tag") {
        this.tags.map(tag => {
          if (value.target.indexOf(tag.id) >= 0) {
            tag.meta.showTag = !tag.meta.showTag;
          }
        });
        this.handleModifier(value);
      }
    },
    handleModifier(action) {
      if (action.modifier) {
        if (action.modifier === "play") {
          this.$refs.video.play();
        }
        if (action.modifier === "pause") {
          this.$refs.video.pause();
        }

        if (action.modifier === "toggle") {
          if (this.$refs.video.paused) {
            this.$refs.video.play();
          } else {
            this.$refs.video.pause();
          }
        }
      }
    },
    handleAssetLoad(value) {
      console.log("handleAssetLoad", value);
      const that = this;
      fetch(`${apiBaseUrl}/api/video/data?assetId=${value.target}`, {
        method: "get"
      })
        .then(res => {
          return res.json();
        })
        .then(function(data) {
          console.log("asset is loaded", data);
          // now set the state to update all the tags and the video source
          that.$root.$_selectedLanguage = that.selectedLanguage;
          that.state = data;
          if (that.state.subtitles) {
            that.state.subtitles.map(subtitle => {
              if (subtitle.file !== "") {
                that.hasSubtitles = true;
              } else {
                that.hasSubtitles = false;
              }
            });
          }
          that.$nextTick(() => {
            // time is stored in value of the action object
            let t = 0;
            try {
              t = parseFloat(value.value);
            } catch (err) {
              console.error("failed parse time from value", t);
            }
            that.$refs.video.currentTime = t;
            that.handleModifier(value);
            that.setCrossOrigin();
          });
        })
        .catch(e => {
          console.error(e);
        });
    },
    handlePostMessage(action) {
      let overlay = this.$refs.overlay;
      let childs = (overlay || []).$children || [];
      childs.forEach(child => {
        if (child && child.$refs.iFrame) {
          let cif = child.$refs.iFrame;
          if (cif && child.postMessageToiFrame) {
            child.postMessageToiFrame(action.value);
          }
        }
      });
    },
    onCloseIframe() {
      this.iframeUrl = null;
      this.$refs.video.play();
    },
    onVideoEnded(ev) {
      console.log("on video ended", ev);
      this.videoEnded = true;
      this.trackVideo("ended", this.$refs.video.currentTime);

      this.onSaveScormData(0);
      if (this.inLMS) {
        setScore(this.getQuizScore());
        finishScormSession();
      }
    },
    getQueryVariable(variable) {
      var query = window.location.search.substring(1);
      var vars = query.split("&");
      for (var i = 0; i < vars.length; i++) {
        var pair = vars[i].split("=");
        if (pair[0] === variable) {
          return pair[1];
        }
      }
      return false;
    },
    // handle messages from embedded iFrames
    handleMessage(event) {
      // console.log('handleMessage', event)
      try {
        var data = event.data;
        if (data.action) {
          this.onAction(data.action);
          this.onCloseIframe();
        } else if (data.type === "alert") {
          this.alertMessage = data.message;
        } else if (!data.url && data.playAt) {
          this.$refs.video.currentTime = data.playAt;
          this.onCloseIframe();
        } else if (data.url) {
          this.onOpen({
            target: "_self",
            value: data.url + "?playAt=" + data.playAt || 0
          });
        } else if (data.type === "playebackRate") {
          this.$refs.video.playbackRate = data.playbackRate;
        }
      } catch (error) {
        console.log("failed to process message", event);
      }
    },
    onViewersScoreChange(newVal, oldVal) {
      console.log("onViewersScoreChange", newVal, oldVal);
    }
  }
};
</script>
<style scoped lang="scss">
.viewer {
  * {
    box-sizing: border-box;
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Safari */
    -khtml-user-select: none; /* Konqueror HTML */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* Internet Explorer/Edge */
    user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome and Opera */
  }
  font-family: "Open Sans", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  position: relative;
  padding: 0;
  margin: 0;
  .viewer-controls {
    position: absolute;
    z-index: 9999;
    bottom: 5px;
    width: 100%;
  }
  .viewer_wrapper {
    // max-width: 1280px;
    max-width: 100%;
    video: {
      width: 100%;
      max-width: 100%;
    }
  }
  .alert-message {
    position: absolute;
    top: 25%;
    left: 50%;
    background-color: crimson;
    padding: 0.5em;
    /* margin-left: -50%; */
    transform: translate(-50%, -50%);
    animation: flash 2s infinite;
    i,
    span {
      vertical-align: middle;
      color: white;
    }
    i {
      padding-right: 5pt;
      color: orange;
    }
  }

  .splash,
  .play-splash,
  .embedIFrame {
    box-shadow: 0px 0px 20px 5px rgba(0, 0, 0, 0.5);
    position: absolute;
    top: 20px;
    left: 20px;
    bottom: 20px;
    right: 36px;
    z-index: 1001;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.8);
    h1 {
      background-color: white;
      padding: 10pt;
      font-weight: 700;
      text-transform: uppercase;
      margin: 10pt;
    }
    .play-icon {
      width: 64pt;
      height: 64pt;
      font-size: 64pt;
      color: #fff;
      background-color: #c91518;
      margin: 10pt;
      cursor: pointer;
    }
  }
  // override defaults for play-splash
  .play-splash {
    box-shadow: none;
    background: transparent; // rgba(0,0,0,0.2);
    position: absolute;
    top: 0px;
    left: 0px;
    bottom: 5px;
    right: 0px;
  }
  .embedIFrame {
    .close {
      cursor: pointer;
      position: absolute;
      top: -1px;
      right: -33px;
      width: 2em;
      height: 2em;
      padding: 0.25em;
      text-align: center;
      vertical-align: middle;
      background-color: black;
      color: crimson;
    }
  }
  &.fullscreen {
    background: black;
    height: 100%;
    width: 100%; // position: absolute;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    .viewer_wrapper {
      position: relative;
      width: 100%;
      max-width: 100%; // border: 5px solid green;
    }
    video {
      width: 100%;
      max-width: 100%;
      height: 100%; // was calc(100% - 40px), why? Now, we want the video over the full space, so that all caclulations are correct.
    }
  }
  .sepia-filter {
    filter: sepia(1);
  }
  .grayscale-filter {
    filter: grayscale(1);
  }
  .lighten-filter {
    filter: brightness(2);
  }
  .darken-filter {
    filter: brightness(0.5);
  }
}

@keyframes flash {
  from,
  50%,
  to {
    opacity: 1;
  }

  25%,
  75% {
    opacity: 0;
  }
}

.cling-error {
  text-align: center;
  margin-top: 20px;
}
</style>
